// FAQs page with common questions and cross-references to Discover pages

import React, { useState } from 'react';
import { Link } from 'react-router';
import { SearchIcon, XIcon } from 'lucide-react';
import type { FAQItem } from '../types/faq';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import SEO from '../seo/SEO';
import { BRAND_LINKS, buildSupportMailto } from '@airhead-games/shared/configs';

const FAQs: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const faqItems: FAQItem[] = [
    // === ABOUT AIRHEAD GAMES ===
    {
      question: 'What is Airhead Games?',
      schemaAnswer:
        'Airhead Games is an online party game platform built for streamers and their communities. Games are quick, fresh every session, and frictionless for players to join from any browser. Airhead Games can also be played with remote teams, friend groups, game nights, birthday parties, and more.',
      answer: (
        <>
          Airhead Games is an online party game platform built for streamers and
          their communities. Games are quick, fresh every session, and
          frictionless for players to join from any browser. Airhead Games can
          also be played with remote teams, friend groups, game nights, birthday
          parties, and more. Check out{' '}
          <Link to="/discover/games">our games.</Link>
        </>
      ),
    },
    {
      question: 'Is Airhead Games legit?',
      schemaAnswer: `Yes! Airhead Games is recognized by IGDB (The Internet Game Database), has an official Twitch category (${BRAND_LINKS.twitchDirectory}), and uses Stripe and Twitch for secure payment and authentication integrations. Check out our gameplay clips at airheadgames.com/discover/clips.`,
      answer: (
        <>
          Yes! Airhead Games is recognized by{' '}
          <a
            href="https://www.igdb.com/games/airhead-games"
            target="_blank"
            rel="noopener noreferrer"
          >
            IGDB
          </a>{' '}
          (The Internet Game Database), has an official{' '}
          <a
            href={BRAND_LINKS.twitchDirectory}
            target="_blank"
            rel="noopener noreferrer"
          >
            Twitch category
          </a>
          , and uses Stripe and Twitch for secure payment and authentication
          integrations. Check out <Link to="/discover/clips">Clips</Link> to see
          our games in action!
        </>
      ),
    },
    {
      question: 'How many players can play?',
      schemaAnswer:
        'Our games support between 1 and 16 players, depending on the game. Each game has a specific minimum and maximum player count. Check the game library at airheadgames.com/discover/games for more details.',
      answer: (
        <>
          Our games support between 1 and 16 players, depending on the game.{' '}
          Each game has a specific minimum and maximum player count. Discover
          the <Link to="/discover/games">game library</Link> for more details.
        </>
      ),
    },
    {
      question: 'How long do games take?',
      schemaAnswer:
        'Most games last between 10 and 20 minutes, although it depends on the number of players and how responsive they are. Quick enough to keep your stream or meeting moving, but long enough to actually have fun. Check individual game durations at airheadgames.com/discover/games.',
      answer: (
        <>
          We aim to have each game last between 10 and 20 minutes, although it
          depends on the number of players and how responsive they are. Quick
          enough to keep your stream or meeting moving, but long enough to
          actually have fun. Discover each game&apos;s average duration in{' '}
          <Link to="/discover/games">our games</Link>.
        </>
      ),
    },
    {
      question: 'Who is behind Airhead Games?',
      schemaAnswer:
        'Airhead Games is built by an independent solo developer. Learn more at airheadgames.com/discover/games.',
      answer: (
        <>
          Airhead Games is built by an independent solo developer.{' '}
          <Link to="/discover/games#history">Learn more</Link> about our story.
        </>
      ),
    },

    // === PRICING & CREDITS ===
    {
      question: 'Are Airhead Games free to play?',
      schemaAnswer:
        'Games are always free for players. Hosts use game credits, called Loons, to start a game. Every new user gets some free Loons when they sign up and more can be purchased as needed.',
      answer: (
        <>
          Games are always free for players. Hosts use game credits, called{' '}
          <Link to="/discover/features/loons">Loons</Link>, to start a game.
          Every new user gets some free Loons when they sign up and more can be
          purchased as needed. <Link to="/login">Login</Link> to create your
          account and claim your free Loons.
        </>
      ),
    },
    {
      question: 'What is your business model?',
      schemaAnswer:
        'Airhead Games runs on Loons, our game credit system. New accounts get some free Loons to get started. Buy what you need, no subscription or commitment required. See airheadgames.com/pricing for credit packages. We believe game credits are the fairest exchange of value: costs are predictably tied to the games you play, and same for us. That keeps every game fresh and tailored to your group.',
      answer: (
        <>
          Airhead Games runs on <Link to="/discover/features/loons">Loons</Link>
          , our game credit system. We offer new accounts some free Loons to get
          started, then you can buy more to keep the party going. Buy what you
          need, no subscription or commitment required. See our{' '}
          <Link to="/pricing">Pricing</Link> page for credit packages.
          <br />
          <br />
          We believe game credits are the fairest exchange of value: costs are
          predictably tied to the games you play... and same for us! That&apos;s
          the price of keeping every game fresh and tailored to your group.
        </>
      ),
    },
    {
      question: 'Do Loons expire?',
      schemaAnswer:
        'No. Your Loon balance never expires. Stock up before a big session or buy as you go; your Loons will be there whenever you are ready to host.',
      answer: (
        <>
          No, your <Link to="/discover/features/loons">Loon</Link> balance never
          expires. Stock up before a big session or buy as you go; your Loons
          will be there whenever you are ready to host.
        </>
      ),
    },
    {
      question: 'How do achievements work?',
      schemaAnswer:
        'Achievements reward you for things you do on Airhead Games, such as hosting games and connecting your Twitch account. Each achievement has one or more levels, and each level has its own reward. Past activity counts too, so you may have already earned some. Log in and open Achievements in the Game Library to see your progress.',
      answer: (
        <>
          <Link to="/discover/features/achievements">Achievements</Link> reward
          you for things you do on Airhead Games, such as hosting games and
          connecting your Twitch account. Each achievement has one or more
          levels, and each level has its own reward. Past activity counts too,
          so you may have already earned some. Log in and open{' '}
          <Link to="/library/achievements">Achievements</Link> in the Game
          Library to see your progress.
        </>
      ),
    },

    // === GETTING STARTED ===
    {
      question: 'Do players need an account or download?',
      schemaAnswer:
        'No. Players join instantly in any browser with no account, download, or app required. The host shares a link or QR code and players join on their own device in seconds. Only the host needs an account.',
      answer: (
        <>
          No. Players join instantly in any browser: no accounts, downloads,
          installs, or apps required. The host shares a link or QR code, players
          join from their own devices, pick a name and avatar, and they are
          ready to play. Only the host needs an account.
        </>
      ),
    },
    {
      question: 'How do I log in?',
      schemaAnswer:
        'Authentication is handled with either a one-time passcode (sent via email) or Twitch. Both options get you into our games quickly and securely without passwords. New accounts receive free Loons so you can start playing right away. Learn more about Login Options at airheadgames.com/discover/features/login.',
      answer: (
        <>
          Authentication is handled with either a one-time passcode (sent via
          email) or Twitch. Both options get you into our games quickly and
          securely without passwords. New accounts receive free{' '}
          <Link to="/discover/features/loons">Loons</Link> so you can start
          playing right away. Learn more about the different{' '}
          <Link to="/discover/features/login">Login Options</Link>.
        </>
      ),
    },
    {
      question: 'How do I play?',
      schemaAnswer:
        'Only the host needs Loons to start a game; everyone else joins free. No downloads, installs, or accounts for players. Simply share a link or QR code, and everyone joins instantly from their desktop or mobile browser. Read about creating a room at airheadgames.com/discover/features/game-room-lifecycle to get started.',
      answer: (
        <>
          Only the host needs <Link to="/discover/features/loons">Loons</Link>{' '}
          to start a game; everyone else joins free. No downloads, installs, or
          accounts for players. Simply share a link or QR code, and everyone
          joins instantly from their desktop or mobile browser. Read more about{' '}
          <Link to="/discover/features/game-room-lifecycle#creating-a-room">
            creating a room
          </Link>{' '}
          to get started.
        </>
      ),
    },
    {
      question: 'How do players join my game?',
      schemaAnswer:
        'Players can join instantly with no account or download required. Learn about all the ways to share and join a game room at airheadgames.com/discover/features/game-room-lifecycle.',
      answer: (
        <>
          Players can join instantly with no account or download required. Learn
          about all the ways to{' '}
          <Link to="/discover/features/game-room-lifecycle#joining-a-room">
            share and join a game room
          </Link>
          .
        </>
      ),
    },

    // === HOSTING & STREAMING ===
    {
      question: 'How do I host a game?',
      schemaAnswer:
        'Open Airhead Games in a browser window and select a game to create a room. Share your screen to Twitch, Discord, Zoom, Google Meet, or cast to a TV for in-person play. Players join via link or QR code on their own devices. Read more about the Game Room Lifecycle at airheadgames.com/discover/features/game-room-lifecycle.',
      answer: (
        <>
          Open Airhead Games in a browser window, and select a game to create a
          room. Share your screen to Twitch, Discord, Zoom, Google Meet, or cast
          to a TV for in-person play. Players join via link or QR code on their
          own devices. It&apos;s simple! If you want to dive deeper, read more
          about the{' '}
          <Link to="/discover/features/game-room-lifecycle">
            Game Room Lifecycle
          </Link>{' '}
          features.
        </>
      ),
    },
    {
      question: 'Can I play with friends remotely?',
      schemaAnswer:
        'Yes. Airhead Games is designed for remote play. The host shares their screen over Twitch, Discord, Zoom, Google Meet, or any video call. Players join from their own devices from anywhere in the world using a link or QR code.',
      answer: (
        <>
          Absolutely. Airhead Games is designed for remote play. The host runs
          the game in their browser and shares their screen over Twitch,
          Discord, Zoom, Google Meet, or wherever your group hangs out. Players
          join from their own devices using a link or QR code—no matter where in
          the world they are.
        </>
      ),
    },
    {
      question: 'Can I play with friends in person?',
      schemaAnswer:
        'Definitely. Hosts can run Airhead Games from any browser-enabled device and cast or share their screen for the room to see. Players join from their own devices using a link or QR code.',
      answer: (
        <>
          Definitely. Users can host Airhead Games from any browser-enabled
          device, and players join from their own devices using a link or QR
          code.
        </>
      ),
    },
    {
      question: 'Any tips for streaming?',
      schemaAnswer:
        'Review the Moderator Tools at airheadgames.com/discover/features/moderator-tools and set Volume Controls at airheadgames.com/discover/features/audio#audio-experience__volume-controls that work with your specific Twitch setup. Open a second browser window or join from your phone to play along with your community. Read aloud instructions, prompts, and reveals to keep your audience informed and engaged.',
      answer: (
        <>
          Review the{' '}
          <Link to="/discover/features/moderator-tools">Moderator Tools</Link>{' '}
          available and set{' '}
          <Link to="/discover/features/audio#audio-experience__volume-controls">
            Volume Controls
          </Link>{' '}
          that work with your specific Twitch setup. Open a second browser
          window or join from your phone to play along with your community. Read
          aloud instructions, prompts, and reveals to keep your audience
          informed and engaged as the game is played.
        </>
      ),
    },
    {
      question: 'How does Twitch integration work?',
      schemaAnswer:
        "Airhead Games detects when you're playing our games live on Twitch, announces your streams to Discord and X, and offers Twitch-only features. Poppy can post your join link and answer viewer commands in your chat. Learn more about Twitch Integrations at airheadgames.com/discover/features/twitch.",
      answer: (
        <>
          Airhead Games detects when you&apos;re playing our games live on
          Twitch, announce your streams to Discord and X, and offer Twitch-only
          features. Poppy can post your join link and answer viewer commands in
          your chat. Learn more about our{' '}
          <Link to="/discover/features/twitch">Twitch Integrations</Link>.
        </>
      ),
    },
    {
      question: 'Is the music stream-safe?',
      schemaAnswer:
        'Yes! All tracks are our own original music. It is royalty-free for streaming and you will not get copyright strikes by using it. Explore the full music catalog at airheadgames.com/discover/music.',
      answer: (
        <>
          Yes! All tracks are our own original music. It is royalty-free for
          streaming and you will not get copyright strikes by using it. Explore
          our entire <Link to="/discover/music">music catalog</Link> or learn
          how the{' '}
          <Link to="/discover/features/audio#music">music features</Link> work.
        </>
      ),
    },
    {
      question: 'How do moderation tools work?',
      schemaAnswer:
        'Control who can join with player access controls and manage profanity censoring. Explore Moderator Tools at airheadgames.com/discover/features/moderator-tools.',
      answer: (
        <>
          Control who can join with player access controls, manage profanity
          censoring. Explore{' '}
          <Link to="/discover/features/moderator-tools">Moderator Tools</Link>.
        </>
      ),
    },
    {
      question: 'Can I remove or block players from my games?',
      schemaAnswer:
        "Yes. Hosts can remove individual players from the room via Poppy's Menu. You can also require Twitch login to join your games and prevent blocked and banned Twitch users from joining them. Visit Player Access settings at airheadgames.com/discover/features/moderator-tools to learn more.",
      answer: (
        <>
          Yes. Hosts can remove individual players from the room via{' '}
          <Link to="/discover/features/poppy#in-game-menu">
            Poppy&apos;s Menu
          </Link>
          . You can also require Twitch login to join your games and prevent
          blocked and banned Twitch users from joining them. Visit the{' '}
          <Link to="/discover/features/moderator-tools#player-access">
            Player Access
          </Link>{' '}
          settings to learn more.
        </>
      ),
    },
    {
      question: 'Can I customize my experience?',
      schemaAnswer:
        'Yes! Adjust host style, content tone, and language in your preferences. Explore all User Preferences features at airheadgames.com/discover/features/user-preferences to personalize your games.',
      answer: (
        <>
          Yes! Adjust host style, content tone, and language in your
          preferences. Explore all of the{' '}
          <Link to="/discover/features/user-preferences">User Preferences</Link>{' '}
          features available to personalize your games.
        </>
      ),
    },
    {
      question: 'Is Airhead Games good for team building?',
      schemaAnswer:
        "Yes. Airhead Games is a great tool for remote team icebreakers, meeting warmups, and virtual team building. Games like Finish Each Other's Sandwiches are built around collaboration and naturally spark conversations about how well your team thinks alike. No setup required—just share a link and everyone joins from their browser.",
      answer: (
        <>
          Yes! Airhead Games is a great way to energize remote teams, break the
          ice in meetings, or close out a work session on a high note. Games
          like{' '}
          <Link to="/discover/games/finish-each-others-sandwiches">
            Finish Each Other&apos;s Sandwiches
          </Link>{' '}
          are built around collaboration and naturally spark honest
          conversations about how well your team thinks alike. No setup
          required—just share a link and everyone joins from their browser.
        </>
      ),
    },

    // === POLICIES & PRIVACY ===
    {
      question: 'How do you use AI?',
      schemaAnswer:
        'Poppy generates game content and commentary on the spot every single session. AI is also used to create music, sound effects, and support the operations of Airhead Games. Learn more in our Terms at airheadgames.com/terms.',
      answer: (
        <>
          Poppy generates game content and commentary on the spot every single
          session. AI is also used to create music, sound effects, and support
          the operations of Airhead Games. Learn more in our{' '}
          <Link to="/terms#ai-generated-content">Terms</Link>.
        </>
      ),
    },
    {
      question: 'What should I know about terms and privacy?',
      schemaAnswer:
        'You must be 13 or older to use Airhead Games. We collect minimal data and support GDPR/CCPA deletion requests. Our full Terms and Conditions at airheadgames.com/terms cover eligibility, content policies, and privacy and data handling.',
      answer: (
        <>
          You must be 13 or older to use Airhead Games. We collect minimal data
          and support GDPR/CCPA deletion requests. Our full{' '}
          <Link to="/terms">Terms & Conditions</Link> cover eligibility, content
          policies, and{' '}
          <Link to="/terms#privacy">privacy and data handling</Link>.
        </>
      ),
    },

    // === CATCH-ALL ===
    {
      question: "Can't find what you're looking for? Need help?",
      schemaAnswer: `Reach out via email at ${BRAND_LINKS.supportEmail} or join our Discord community at ${BRAND_LINKS.discord}.`,
      answer: (
        <>
          Reach out via <a href={buildSupportMailto()}>email</a> or join us in{' '}
          <a
            href={BRAND_LINKS.discord}
            target="_blank"
            rel="noopener noreferrer"
          >
            Discord
          </a>
          .
        </>
      ),
    },
  ];

  const filteredItems = searchQuery.trim()
    ? faqItems.filter(
        (item) =>
          item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.schemaAnswer?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqItems;

  return (
    <div className="min-h-svh bg-background text-foreground">
      <SEO faqItems={faqItems} />
      <main className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 pb-12 pt-28 md:gap-10 md:px-8 md:pb-16 md:pt-32 lg:px-12">
        <section className="mx-auto flex w-full max-w-4xl flex-col gap-6">
          <div className="flex flex-col gap-3 text-center md:gap-4">
            <h1 className="font-heading text-4xl leading-tight tracking-tight md:text-5xl">
              Frequently Asked Questions
            </h1>
            <p className="text-base leading-6 text-muted-foreground md:text-lg">
              Quick answers to the most common questions
            </p>
          </div>
          <InputGroup className="mx-auto w-full max-w-2xl">
            <InputGroupInput
              type="text"
              placeholder="Search FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <InputGroupAddon align="inline-start" aria-hidden="true">
              <SearchIcon />
            </InputGroupAddon>
            {searchQuery && (
              <InputGroupAddon align="inline-end">
                <InputGroupButton
                  type="button"
                  size="icon-xs"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <XIcon />
                </InputGroupButton>
              </InputGroupAddon>
            )}
          </InputGroup>
        </section>

        <section className="mx-auto w-full max-w-4xl">
          {filteredItems.length > 0 ? (
            <Accordion
              type="single"
              collapsible
              className="w-full border-y border-border/80"
            >
              {filteredItems.map((item, index) => (
                <AccordionItem key={item.question} value={`item-${index}`}>
                  <AccordionTrigger className="font-heading text-lg leading-snug font-medium hover:text-primary">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="text-base leading-7 text-muted-foreground [&_a]:cursor-pointer [&_a]:text-primary [&_a]:underline-offset-4 [&_a:hover]:underline">
                      {item.answer}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <p className="py-10 text-center text-base text-muted-foreground">
              No results for &quot;{searchQuery}&quot;
            </p>
          )}
        </section>
      </main>
    </div>
  );
};

export default FAQs;
